import type { DownloadView, MovieDetail, VariantView } from '@zyx/shared';
import { useQueryClient } from '@tanstack/react-query';
import Hls from 'hls.js';
import { Airplay, HardDrive, MapPin, Play, Radio, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useApi, useConnection } from '@/hooks/use-connection';
import { useCreateBookmark } from '@/hooks/use-library';
import { editionLabel, formatRuntime, qualityLabel } from '@/lib/format';
import { queryKeys } from '@/lib/query-keys';
import { AssetImage } from './asset';

/** What to play: an episode of a variant, from a time. `nonce` restarts it when the same spot is asked for twice. */
export interface PlayRequest {
  readonly variantId: number;
  readonly episodeNo: number;
  readonly startS: number;
  readonly nonce: number;
}

let requests = 0;

/** A fresh `nonce` for each play request, so asking for the same spot again restarts it. */
export function nextNonce(): number {
  requests += 1;
  return requests;
}

/**
 * Safari's AirPlay for web video: the one engine with it (Safari on the Mac, and every
 * browser on iPhone and iPad). Its player then plays HLS natively.
 */
const AIRPLAY_CAPABLE = typeof window !== 'undefined' && 'WebKitPlaybackTargetAvailabilityEvent' in window;

/** An address only this machine can reach, which an Apple TV cannot fetch from. */
function loopbackBase(url: string): boolean {
  try {
    return ['127.0.0.1', 'localhost', '[::1]'].includes(new URL(url).hostname);
  } catch {
    return false;
  }
}

/** How often the position is saved while playing. */
const SAVE_EVERY_MS = 10_000;

/** Finished offline copies of the movie, any edition and quality. */
export type OfflineCopies = ReadonlyArray<DownloadView>;

/** An episode's offline copies, best quality first. */
function copiesOf(offline: OfflineCopies, variantId: number, episodeNo: number): ReadonlyArray<DownloadView> {
  const height = (copy: DownloadView) => Number(/x(\d+)$/.exec(copy.resolution)?.[1] ?? 0);
  return offline.filter((copy) => copy.variantId === variantId && copy.episodeNo === episodeNo).sort((left, right) => height(right) - height(left));
}

/** Every part streams, or every part is kept offline: a gone edition plays only the second way. */
function playable(variant: VariantView, offline: OfflineCopies): boolean {
  if (variant.episodes.length === 0) {
    return false;
  }
  if (variant.episodes.every((episode) => copiesOf(offline, variant.variantId, episode.episodeNo).length > 0)) {
    return true;
  }
  return variant.gone !== true && variant.episodes.every((episode) => episode.status === 'ok' || episode.status === 'unchecked');
}

/**
 * Where pressing play starts: where the viewer left off, if they have not finished and
 * that edition can still play; otherwise the default edition from the beginning.
 */
export function startingPoint(movie: MovieDetail, offline: OfflineCopies = []): PlayRequest | undefined {
  const progress = movie.progress;
  const resumable = progress !== undefined && !progress.finished ? movie.variants.find((variant) => variant.variantId === progress.variantId && playable(variant, offline)) : undefined;
  if (resumable !== undefined && progress !== undefined) {
    return { variantId: resumable.variantId, episodeNo: progress.episodeNo, startS: progress.positionS, nonce: 0 };
  }
  const variant = movie.variants.find((candidate) => candidate.variantId === movie.primaryVariantId) ?? movie.variants.find((candidate) => playable(candidate, offline));
  return variant === undefined ? undefined : { variantId: variant.variantId, episodeNo: variant.episodes[0]?.episodeNo ?? 1, startS: 0, nonce: 0 };
}

/**
 * One episode through hls.js and the service's relay. Mounted per episode (keyed by the
 * request), so switching tears the old stream down completely. Saves the position as it
 * plays, on pause, at the end and when it goes away.
 */
function EpisodeVideo(props: {
  readonly variant: VariantView;
  readonly request: PlayRequest;
  /** Plays this offline copy instead of the stream. */
  readonly offlineId?: number;
  readonly onEnded: () => void;
  readonly onElement: (element: HTMLVideoElement | null) => void;
  /** Whether Safari's own player took it: only then can AirPlay send it to a TV. */
  readonly onNative: (native: boolean) => void;
}) {
  const api = useApi();
  const { connection } = useConnection();
  const client = useQueryClient();
  const video = useRef<HTMLVideoElement>(null);
  const { onElement } = props;
  // Stable, so React hands the element over once rather than on every render.
  const attach = useCallback(
    (element: HTMLVideoElement | null) => {
      video.current = element;
      onElement(element);
    },
    [onElement],
  );
  const [error, setError] = useState<string | undefined>(undefined);
  const episode = props.variant.episodes.find((candidate) => candidate.episodeNo === props.request.episodeNo) ?? props.variant.episodes[0];
  const token = connection?.token;
  const variantId = props.variant.variantId;
  const { startS } = props.request;
  const { offlineId } = props;
  // Plain values, so a refetch of the movie (which saving progress triggers) hands the
  // effect equal dependencies and does not restart the stream.
  const episodeId = episode?.episodeId;
  const episodeNo = episode?.episodeNo;
  // The latest callback, read when the episode ends, without restarting the stream when it changes.
  const onEnded = useRef(props.onEnded);
  const onNative = useRef(props.onNative);
  useEffect(() => {
    onEnded.current = props.onEnded;
    onNative.current = props.onNative;
  });

  useEffect(() => {
    const element = video.current;
    if (element === null || episodeId === undefined || episodeNo === undefined) {
      return;
    }
    const save = (refresh: boolean) => {
      if (!Number.isFinite(element.duration) || element.duration <= 0) {
        return;
      }
      void api
        .recordProgress({ variantId, episodeNo, positionS: element.currentTime, durationS: element.duration })
        .then(() => (refresh ? client.invalidateQueries({ queryKey: queryKeys.movies() }) : undefined))
        .catch(() => undefined);
    };
    const start = () => {
      void element.play().catch(() => undefined);
    };

    let hls: Hls | undefined;
    let cancelled = false;
    const url = offlineId === undefined ? api.playUrl(episodeId) : api.downloadFileUrl(offlineId, 'index.m3u8');
    // Safari plays HLS itself, from a URL that carries its own credential: what lets it
    // hand the stream to an Apple TV, which can send neither the token nor the cookie.
    const playNative = (source: string) => {
      onNative.current(true);
      element.src = source;
      element.addEventListener(
        'loadedmetadata',
        () => {
          if (startS > 0) {
            element.currentTime = startS;
          }
          start();
        },
        { once: true },
      );
    };
    const playHls = () => {
      onNative.current(false);
      hls = new Hls({
        // Given up front, so loading starts at the resume point instead of seeking after.
        startPosition: startS > 0 ? startS : -1,
        // A service that requires a token wants it on every playlist and segment too.
        xhrSetup: (xhr) => {
          if (token !== undefined) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }
        },
      });
      hls.on(Hls.Events.MANIFEST_PARSED, start);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          const status = data.response?.code;
          setError(status === 502 ? 'The video host refused or no longer has this video.' : `Playback stopped (${data.details}).`);
        }
      });
      hls.loadSource(url);
      hls.attachMedia(element);
    };
    if (AIRPLAY_CAPABLE) {
      void api
        .mediaLink(offlineId === undefined ? { kind: 'play', id: episodeId } : { kind: 'download', id: offlineId })
        .then((link) => (cancelled ? undefined : playNative(link)))
        // A service from before media links: play as before, without AirPlay.
        .catch(() => (cancelled ? undefined : Hls.isSupported() ? playHls() : playNative(url)));
    } else if (Hls.isSupported()) {
      playHls();
    } else {
      playNative(url);
    }

    // Safari's own player reports a failure only as an event on the element: a relay
    // error, an expired link or a missing copy would otherwise be a silent black frame.
    const onError = () => {
      if (element.error !== null && element.src !== '') {
        setError(element.error.code === element.error.MEDIA_ERR_SRC_NOT_SUPPORTED ? 'The video could not be loaded.' : 'Playback stopped.');
      }
    };
    element.addEventListener('error', onError);

    const timer = setInterval(() => (element.paused ? undefined : save(false)), SAVE_EVERY_MS);
    const onPause = () => save(true);
    const onEnd = () => {
      save(true);
      onEnded.current();
    };
    element.addEventListener('pause', onPause);
    element.addEventListener('ended', onEnd);
    return () => {
      clearInterval(timer);
      element.removeEventListener('pause', onPause);
      element.removeEventListener('error', onError);
      element.removeEventListener('ended', onEnd);
      save(true);
      cancelled = true;
      hls?.destroy();
    };
  }, [api, client, episodeId, episodeNo, token, variantId, startS, offlineId]);

  return (
    <div className="relative animate-fade-in">
      {/* The browser's own controls: they keep every keyboard shortcut and accessibility feature the player has. */}
      <video ref={attach} x-webkit-airplay="allow" controls playsInline className="aspect-video w-full bg-black" />
      {error === undefined ? null : (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-6 text-center text-sm text-white">
          <p className="flex items-center gap-2">
            <TriangleAlert className="size-4 shrink-0 text-amber-400" />
            {error} Try another edition below.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The movie page's player: the cover and a play button until asked, then the episode
 * the request names. Editions and parts are switched here; scenes elsewhere on the page
 * send their own requests.
 */
export function Player(props: { readonly movie: MovieDetail; readonly offline?: OfflineCopies; readonly request?: PlayRequest; readonly onRequest: (request: PlayRequest) => void }) {
  const { movie, request } = props;
  const offline = props.offline ?? [];
  const variant = request === undefined ? undefined : movie.variants.find((candidate) => candidate.variantId === request.variantId);
  const first = startingPoint(movie, offline);
  // An offline copy plays by default; the viewer may switch to the stream, or another copy.
  const [chosenCopy, setChosenCopy] = useState<number | 'stream' | undefined>(undefined);
  const copies = variant === undefined || request === undefined ? [] : copiesOf(offline, variant.variantId, request.episodeNo);
  const wanted = chosenCopy === 'stream' ? (variant?.gone === true ? copies[0]?.downloadId : undefined) : (copies.find((copy) => copy.downloadId === chosenCopy) ?? copies[0])?.downloadId;
  // Settled when an episode starts, or the viewer switches: a copy that finishes while the
  // stream plays must not restart the video from where the request began.
  const playingKey = request === undefined ? undefined : `${request.variantId}-${request.episodeNo}-${request.nonce}-${String(chosenCopy)}`;
  const [settled, setSettled] = useState<{ readonly key?: string; readonly offlineId?: number }>({});
  if (settled.key !== playingKey) {
    setSettled({ key: playingKey, offlineId: wanted });
  }
  const offlineId = settled.key === playingKey ? settled.offlineId : wanted;
  const choices = movie.variants.filter((candidate) => candidate.episodes.length > 0);
  // The playing element, for reading the moment to bookmark.
  const [element, setElement] = useState<HTMLVideoElement | null>(null);
  const createBookmark = useCreateBookmark();
  const api = useApi();
  // Whether Safari sees an AirPlay receiver for the playing video.
  const [airplay, setAirplay] = useState(false);
  // hls.js's video (MediaSource) cannot be sent to a TV: the button is for Safari's own player only.
  const [native, setNative] = useState(false);
  useEffect(() => {
    if (element === null || !AIRPLAY_CAPABLE) {
      return;
    }
    const onAvailability = (event: Event) => setAirplay('availability' in event && event.availability === 'available');
    element.addEventListener('webkitplaybacktargetavailabilitychanged', onAvailability);
    return () => {
      element.removeEventListener('webkitplaybacktargetavailabilitychanged', onAvailability);
      setAirplay(false);
    };
  }, [element]);
  const variantId = variant?.variantId;
  const episodeNo = request?.episodeNo;

  // B bookmarks the moment on screen, unless the viewer is typing somewhere.
  const bookmarkHere = useRef<() => void>(() => undefined);
  useEffect(() => {
    bookmarkHere.current = () => {
      if (element !== null && variantId !== undefined && episodeNo !== undefined && !createBookmark.isPending) {
        createBookmark.mutate({ variantId, episodeNo, positionS: element.currentTime });
      }
    };
  });
  const playing = variantId !== undefined;
  useEffect(() => {
    if (!playing) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      // Menus and selects use letters to jump between items, so a B there is not for the player.
      const typing =
        event.defaultPrevented ||
        (target instanceof HTMLElement &&
          (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.closest('[role="menu"], [role="listbox"], [role="dialog"], [role="alertdialog"]') !== null));
      if (event.key.toLowerCase() === 'b' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey && !event.repeat) {
        event.preventDefault();
        bookmarkHere.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playing]);

  if (request === undefined || variant === undefined) {
    return (
      <div className="space-y-3">
        <button
          type="button"
          disabled={first === undefined}
          onClick={() => first !== undefined && props.onRequest({ ...first, nonce: nextNonce() })}
          className="group relative block w-full overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed"
          aria-label={first === undefined ? 'Nothing playable' : 'Play'}
        >
          <AssetImage file={movie.cover} alt={movie.code} className="aspect-[3/2] w-full" />
          {first === undefined ? null : (
            <span className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/30">
              <span className="cta flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold shadow-card-hover transition-theme group-hover:scale-105">
                <Play className="size-5 fill-current" />
                {first.startS > 0 ? `Resume at ${formatRuntime(first.startS)}` : 'Play'}
              </span>
            </span>
          )}
        </button>
        {first === undefined ? <p className="px-3 pb-3 text-sm text-muted-foreground">No edition of this movie can be played right now.</p> : null}
      </div>
    );
  }

  const episodeIndex = variant.episodes.findIndex((episode) => episode.episodeNo === request.episodeNo);
  const next = variant.episodes[episodeIndex + 1];
  return (
    <div className="space-y-3">
      <EpisodeVideo
        key={`${request.variantId}-${request.episodeNo}-${request.nonce}-${offlineId ?? 'stream'}`}
        variant={variant}
        request={request}
        offlineId={offlineId}
        onElement={setElement}
        onNative={setNative}
        onEnded={() => (next === undefined ? undefined : props.onRequest({ variantId: variant.variantId, episodeNo: next.episodeNo, startS: 0, nonce: nextNonce() }))}
      />
      <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
        <Button size="sm" variant="outline" onClick={() => bookmarkHere.current()} disabled={createBookmark.isPending} title="Bookmark this moment (B)">
          <MapPin />
          Bookmark
          <kbd className="rounded border border-border px-1 font-mono text-[10px] text-muted-foreground">B</kbd>
        </Button>
        {airplay && native ? (
          <Button
            size="sm"
            variant="outline"
            title="Play on an Apple TV or AirPlay screen"
            onClick={() => {
              // The TV fetches the video itself, from the address this app uses for zyx.
              if (loopbackBase(api.baseUrl)) {
                toast.warning('The TV cannot reach zyx at this address (127.0.0.1). Connect this app to zyx by its network name, such as https://mac-studio-local.qinnan.dev, then AirPlay.');
                return;
              }
              if (element !== null && 'webkitShowPlaybackTargetPicker' in element && typeof element.webkitShowPlaybackTargetPicker === 'function') {
                element.webkitShowPlaybackTargetPicker();
              }
            }}
          >
            <Airplay />
            AirPlay
          </Button>
        ) : null}
        {choices.length > 1
          ? choices.map((choice) => (
              <Button
                key={choice.variantId}
                size="sm"
                variant={choice.variantId === variant.variantId ? 'default' : 'outline'}
                onClick={() => props.onRequest({ variantId: choice.variantId, episodeNo: choice.episodes[0].episodeNo, startS: 0, nonce: nextNonce() })}
              >
                {editionLabel(choice.edition)}
                {playable(choice, offline) ? null : <TriangleAlert className="size-3.5" />}
              </Button>
            ))
          : null}
        {copies.length === 0 ? null : (
          <div className="flex gap-1" role="group" aria-label="Play from">
            {copies.map((copy) => (
              <Button
                key={copy.downloadId}
                size="sm"
                variant={offlineId === copy.downloadId ? 'secondary' : 'ghost'}
                onClick={() => setChosenCopy(copy.downloadId)}
                title="The copy kept on zyx's disk"
              >
                <HardDrive />
                {qualityLabel(copy.resolution)}
              </Button>
            ))}
            {variant.gone === true ? null : (
              <Button size="sm" variant={offlineId === undefined ? 'secondary' : 'ghost'} onClick={() => setChosenCopy('stream')} title="From the source, through zyx">
                <Radio />
                Stream
              </Button>
            )}
          </div>
        )}
        {variant.episodes.length > 1 ? (
          <div className="ml-auto flex gap-1">
            {variant.episodes.map((episode) => (
              <Button
                key={episode.episodeId}
                size="sm"
                variant={episode.episodeNo === request.episodeNo ? 'secondary' : 'ghost'}
                onClick={() => props.onRequest({ variantId: variant.variantId, episodeNo: episode.episodeNo, startS: 0, nonce: nextNonce() })}
              >
                Part {episode.episodeNo}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
